/**
 * RiskScoreBadge — shared chip showing a risk's level color + numeric exposure.
 *
 * Reused by every page in the Risk Management module (worksheet, summary,
 * dashboard, timeline) so colors stay consistent.
 *
 * Exposure formula (server-authoritative, mirrored on the client):
 *   exposure = impact * probability             (integer 1-100 for 1-10 inputs)
 *   level:    exposure >= 49 -> HIGH
 *             exposure >= 16 -> MEDIUM
 *             else           -> LOW
 */

import React from "react";
import { Chip, Tooltip, Box, Typography } from "@mui/material";
import type { RiskLevel } from "../Types/RiskManagementTypes";

interface RiskScoreBadgeProps {
  level: RiskLevel | null | undefined;
  /**
   * Accepts either a number or a numeric string — DRF's `DecimalField`
   * serializes to a string by default ("3.40") so consumers don't have to
   * remember to coerce.
   */
  exposure?: number | string | null;
  showExposure?: boolean;
  size?: "small" | "medium";
  /** If true, the chip is rendered as a compact dot (for dense grids). */
  compact?: boolean;
}

/** Coerce a (possibly stringified) exposure value into a finite number. */
const toExposureNumber = (
  exposure: number | string | null | undefined
): number | null => {
  if (exposure === null || exposure === undefined || exposure === "") return null;
  const n = typeof exposure === "number" ? exposure : Number(exposure);
  return Number.isFinite(n) ? n : null;
};

const LEVEL_META: Record<
  RiskLevel,
  { label: string; bg: string; fg: string; hex: string }
> = {
  HIGH: { label: "HIGH", bg: "#fdecea", fg: "#c62828", hex: "#c62828" },
  MEDIUM: { label: "MED", bg: "#fff4e5", fg: "#ed6c02", hex: "#ed6c02" },
  LOW: { label: "LOW", bg: "#e8f5e9", fg: "#2e7d32", hex: "#2e7d32" },
};

/**
 * Pure helper to classify an exposure value into a `RiskLevel`. Exported so
 * the workspace page can live-recompute the level as the PM edits cells.
 */
export const levelFromExposure = (exposure: number): RiskLevel => {
  if (exposure >= 49) return "HIGH";
  if (exposure >= 16) return "MEDIUM";
  return "LOW";
};

/** Raw `impact * probability` (1-100). Matches the backend formula. */
export const computeExposure = (impact: number, probability: number): number => {
  const safeImpact = Number.isFinite(impact) ? impact : 0;
  const safeProb = Number.isFinite(probability) ? probability : 0;
  return safeImpact * safeProb;
};

/**
 * Worksheet overall risk. Same rule as the worksheet header: the mean of
 * every open risk's exposure, then HIGH ≥ 49, MEDIUM ≥ 16, otherwise LOW.
 */
export const overallFromExposures = (
  exposures: number[]
): { exposure: number; level: RiskLevel } => {
  if (!exposures.length) return { exposure: 0, level: levelFromExposure(0) };
  const exposure =
    exposures.reduce((sum, value) => sum + value, 0) / exposures.length;
  return { exposure, level: levelFromExposure(exposure) };
};

/**
 * Format an exposure value for display. Whole numbers render as plain
 * integers ("32"); anything fractional shows up to 2 decimals ("32.5").
 */
const formatExposure = (value: number): string =>
  Number.isInteger(value) ? String(value) : value.toFixed(2);

export const levelColor = (level: RiskLevel | null | undefined): string =>
  level ? LEVEL_META[level].hex : "#9e9e9e";

const RiskScoreBadge: React.FC<RiskScoreBadgeProps> = ({
  level,
  exposure,
  showExposure = true,
  size = "small",
  compact = false,
}) => {
  if (!level) {
    return (
      <Chip
        label="N/A"
        size={size}
        sx={{ bgcolor: "#eeeeee", color: "#616161", fontWeight: 600 }}
      />
    );
  }

  const meta = LEVEL_META[level];
  const exposureNum = toExposureNumber(exposure);

  if (compact) {
    return (
      <Tooltip
        title={`${meta.label}${
          exposureNum !== null ? ` • exposure ${formatExposure(exposureNum)}` : ""
        }`}
      >
        <Box
          sx={{
            display: "inline-block",
            width: 12,
            height: 12,
            borderRadius: "50%",
            bgcolor: meta.hex,
          }}
        />
      </Tooltip>
    );
  }

  const showNumber = showExposure && exposureNum !== null;

  return (
    <Chip
      size={size}
      label={
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography
            component="span"
            variant="caption"
            sx={{ fontWeight: 700, lineHeight: 1 }}
          >
            {meta.label}
          </Typography>
          {showNumber && (
            <>
              <Typography
                component="span"
                variant="caption"
                sx={{ opacity: 0.6, lineHeight: 1 }}
              >
                /
              </Typography>
              <Typography
                component="span"
                variant="caption"
                sx={{ fontWeight: 600, opacity: 0.95, lineHeight: 1 }}
              >
                {formatExposure(exposureNum as number)}
              </Typography>
            </>
          )}
        </Box>
      }
      sx={{
        bgcolor: meta.bg,
        color: meta.fg,
        border: `1px solid ${meta.fg}33`,
        "& .MuiChip-label": { px: 1 },
      }}
    />
  );
};

export default RiskScoreBadge;
